import React from "react";
import { MapPin } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useCheckoutStore } from "../store/checkout-store";
import { useCheckoutAddresses } from "../hooks/use-checkout-addresses";
import { useTranslation } from "react-i18next";
import { LoadingSpinner } from "@/components/common/loading-spinner";
import { AddressFormInline } from "../components/AddressFormInline";
import { AddressPicker } from "../components/AddressPicker";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";

export const BillingAddressSection: React.FC = () => {
  const { t } = useTranslation();
  const {
    useSameAddressForBilling,
    toggleUseSameAddress,
    selectedDeliveryAddressId,
    isAddingNewBillingAddress,
    isEditingBillingAddress,
    setIsAddingNewBillingAddress,
    setIsEditingBillingAddress,
  } = useCheckoutStore();
  const {
    addresses,
    isLoading,
    selectedBillingAddressId,
    setBillingAddress,
    openDeleteAddressDialog,
  } = useCheckoutAddresses();

  const selectedAddress = useSameAddressForBilling
    ? addresses.find((a) => a._id === selectedDeliveryAddressId)
    : addresses.find((a) => a._id === selectedBillingAddressId);
  const isFormOpen = isAddingNewBillingAddress || isEditingBillingAddress;

  const handleAddNew = () => {
    setIsAddingNewBillingAddress(true);
  };

  const handleCancelForm = () => {
    setIsAddingNewBillingAddress(false);
    setIsEditingBillingAddress(false);
  };

  const handleEditAddress = (addressId: string) => {
    setBillingAddress(addressId);
    setIsEditingBillingAddress(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="md" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "border rounded-[12px] overflow-hidden transition-all",
        isFormOpen
          ? "border-primary/40 ring-1 ring-primary/15 dark:border-primary/50"
          : "border-border-secondary dark:border-gray-700"
      )}
    >
      <div className="bg-bg-secondary dark:bg-gray-800 border-b border-border-secondary dark:border-gray-700 flex items-center justify-between px-3 md:px-4 py-2.5 md:py-3">
        <h2 className="text-base md:text-lg lg:text-[20px] font-semibold leading-6 md:leading-7 lg:leading-[30px] text-text-primary dark:text-text-primary">
          {t("checkout.billingAddress")}
        </h2>
        {!useSameAddressForBilling && !isFormOpen && addresses.length > 0 && (
          <button
            onClick={handleAddNew}
            className="text-sm md:text-md lg:text-[14px] font-semibold text-text-brand hover:opacity-80 transition-colors flex items-center gap-1"
          >
            {t("checkout.addNewAddress")}
          </button>
        )}
      </div>

      <div className="p-3 md:p-4 space-y-3 md:space-y-4">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="useSameAddress"
            checked={useSameAddressForBilling}
            onCheckedChange={toggleUseSameAddress}
          />
          <Label
            htmlFor="useSameAddress"
            className="font-medium cursor-pointer text-xs md:text-sm lg:text-[14px] text-text-primary dark:text-text-primary"
          >
            {t("checkout.useSameAddress")}
          </Label>
        </div>

        <AnimatePresence>
          {!useSameAddressForBilling && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="space-y-3 md:space-y-4">
                {!isFormOpen && addresses.length > 0 && (
                  <AddressPicker
                    addresses={addresses}
                    selectedAddressId={selectedBillingAddressId}
                    onSelect={setBillingAddress}
                    onEdit={handleEditAddress}
                    onDelete={openDeleteAddressDialog}
                  />
                )}

                {!isFormOpen && addresses.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-6 md:py-8 text-center">
                    <MapPin className="w-10 h-10 md:w-12 md:h-12 text-text-tertiary dark:text-gray-400 mb-2 md:mb-3" />
                    <p className="text-xs md:text-sm text-muted-foreground mb-3 md:mb-4">
                      {t("checkout.noAddresses")}
                    </p>
                    <button
                      onClick={handleAddNew}
                      className="text-xs md:text-sm lg:text-[14px] font-semibold text-text-brand hover:opacity-80 transition-colors"
                    >
                      {t("checkout.addNewAddress")}
                    </button>
                  </div>
                )}

                {isFormOpen && (
                  <AddressFormInline
                    mode="billing"
                    editingAddress={
                      isEditingBillingAddress ? selectedAddress : null
                    }
                    onCancel={handleCancelForm}
                  />
                )}
              </div>
            </motion.div>
          )}

          {useSameAddressForBilling && selectedAddress && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="bg-primary/5 dark:bg-primary/10 border border-primary rounded-[12px] p-3"
            >
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center pt-0.5">
                  <div className="w-4 h-4 rounded-full border-2 border-primary bg-white dark:bg-gray-900 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-primary" />
                  </div>
                </div>
                <div className="flex flex-col gap-2 flex-1">
                  <span className="text-xs md:text-sm lg:text-[14px] font-semibold text-text-primary dark:text-text-primary underline">
                    {selectedAddress.label} ({t("checkout.deliveryAddress")})
                  </span>
                  <p className="text-xs md:text-sm lg:text-[14px] font-medium text-text-primary dark:text-text-primary">
                    {selectedAddress.firstName} {selectedAddress.lastName}
                  </p>
                  <div className="text-xs md:text-sm lg:text-[14px] font-normal text-text-secondary dark:text-gray-300 space-y-0.5">
                    <p>{selectedAddress.addressLine1}</p>
                    {selectedAddress.addressLine2 && (
                      <p>{selectedAddress.addressLine2}</p>
                    )}
                    <p>
                      {selectedAddress.city}, {selectedAddress.state}{" "}
                      {selectedAddress.postalCode}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
